import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
} from "react";
import { Mode } from "types/modes";
import { FileItem, OpenTab } from '../types/files';

interface GlobalContextType {
  // Global States
  mode: Mode;
  setMode: (mode: Mode) => void;
  projectName: string | null;
  setProjectName: (name: string | null) => void;
  activeTab: OpenTab | null;
  setActiveTab: React.Dispatch<React.SetStateAction<OpenTab | null>>;


  // File Tree
  fileTree: FileItem[]; // Local file system tree
  setFileTree: React.Dispatch<React.SetStateAction<FileItem[]>>;
  virtualFileTree: FileItem[]; // Session/collaboration file tree
  setVirtualFileTree: React.Dispatch<React.SetStateAction<FileItem[]>>;
  getCurrentFileTree: () => FileItem[]; // Returns appropriate tree based on mode
  activeFile: FileItem | null;
  setActiveFile: React.Dispatch<React.SetStateAction<FileItem | null>>;

  joinedFilePathRef: React.MutableRefObject<string | null>;
  projectRootPath: string | null;
  setProjectRootPath: (path: string | null) => void;
  setProjectRootPathAndProjectName: (path: string | null) => void;
  currentDirectory: string | null;
  setCurrentDirectory: (dir: string | null) => void;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<Mode>(Mode.Local);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<OpenTab | null>(null);
  const joinedFilePathRef = useRef<string | null>(null);
  const [projectRootPath, setProjectRootPath] = useState<string | null>(null);
  const [currentDirectory, setCurrentDirectory] = useState<string | null>(null);

  // File tree-related
  const [fileTree, setFileTree] = useState<FileItem[]>([]);
  const [virtualFileTree, setVirtualFileTree] = useState<FileItem[]>([]);
  //const [currentFileTree, setCurrentFileTree] = useState<FileItem[]>([]);
  const [activeFile, setActiveFile] = useState<FileItem | null>(null);
  
  const setProjectRootPathAndProjectName = (path: string | null) => {
    setProjectRootPath(path);
    if (path) {
      const segments = path.split(/[\\/]/);
      const last = segments[segments.length - 1];
      setProjectName(last || "project");
    } else {
      setProjectName(null);
    }
  };

  /**
   * Returns the appropriate file tree based on current mode:
   * - Mode.Local: returns fileTree (local file system)
   * - Mode.Host/Join: returns virtualFileTree (session file structure)
   */
  const getCurrentFileTree = (): FileItem[] => {
    return mode === Mode.Local ? fileTree : virtualFileTree;
  };

  return (
    <GlobalContext.Provider
      value={{
        mode,
        setMode,
        projectName,
        setProjectName,
        activeTab,
        setActiveTab,
        fileTree,
        setFileTree,
        virtualFileTree,
        setVirtualFileTree,
        getCurrentFileTree,
        activeFile,
        setActiveFile,
        joinedFilePathRef,
        projectRootPath,
        setProjectRootPath,
        setProjectRootPathAndProjectName,
        currentDirectory,
        setCurrentDirectory,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = (): GlobalContextType => {
  const ctx = useContext(GlobalContext);
  if (!ctx)
    throw new Error("useGlobalContext must be used within a GlobalProvider");
  return ctx;
};
